/**
 * Period / date-range helpers shared by the Reports page and the report
 * comparison view. Extracted verbatim from Reports.tsx so both the single
 * report picker and the source/target comparison pickers use one source of
 * truth. All dates are ISO YYYY-MM-DD strings.
 */

export type DateRange = { from: string; to: string };
export type PeriodBounds = { earliest: string | null; latest: string | null };

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function firstOfMonth(): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)).toISOString().slice(0, 10);
}

export function firstOfYear(): string {
  const d = new Date();
  return new Date(Date.UTC(d.getUTCFullYear(), 0, 1)).toISOString().slice(0, 10);
}

export function lastMonthRange(): DateRange {
  const d = new Date();
  const from = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() - 1, 1));
  // Day 0 of current month = last day of previous month.
  const to = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 0));
  return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
}

export function lastYearRange(): DateRange {
  const y = new Date().getUTCFullYear() - 1;
  return { from: `${y}-01-01`, to: `${y}-12-31` };
}

export const MONTH_LABEL = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

/**
 * First and last day of a YYYY-MM string. If the month is the *current*
 * calendar month, the range stops at today (matches the "This month"
 * preset convention) — selecting June 2026 in June shouldn't run to
 * Jun 30 when we're only on Jun 6.
 */
export function monthRange(ym: string): DateRange {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return { from: '', to: '' };
  const from = `${ym}-01`;
  const today = new Date();
  const ty = today.getUTCFullYear();
  const tm = today.getUTCMonth() + 1;
  if (y === ty && m === tm) {
    return { from, to: todayIso() };
  }
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return { from, to: `${ym}-${lastDay.toString().padStart(2, '0')}` };
}

/**
 * Jan 1 → Dec 31 of the chosen year — except the current year stops at
 * today, since "the year 2026 so far" isn't the same as "all of 2026".
 */
export function yearRange(year: number): DateRange {
  const ty = new Date().getUTCFullYear();
  if (year === ty) {
    return { from: `${year}-01-01`, to: todayIso() };
  }
  return { from: `${year}-01-01`, to: `${year}-12-31` };
}

/** Newest-first list of months from `start` (YYYY-MM-DD) through `end`. */
export function monthsBetween(
  startIso: string,
  endIso: string,
): Array<{ value: string; label: string }> {
  if (!startIso || !endIso) return [];
  const [sy, sm] = startIso.slice(0, 7).split('-').map(Number);
  const [ey, em] = endIso.slice(0, 7).split('-').map(Number);
  if (!sy || !sm || !ey || !em) return [];
  const out: Array<{ value: string; label: string }> = [];
  let y = sy;
  let m = sm;
  while (y < ey || (y === ey && m <= em)) {
    const ym = `${y.toString().padStart(4, '0')}-${m.toString().padStart(2, '0')}`;
    out.unshift({ value: ym, label: `${MONTH_LABEL[m - 1]} ${y}` });
    if (m === 12) {
      y += 1;
      m = 1;
    } else {
      m += 1;
    }
  }
  return out;
}

export function yearsBetween(startIso: string, endIso: string): number[] {
  if (!startIso || !endIso) return [];
  const sy = Number(startIso.slice(0, 4));
  const ey = Number(endIso.slice(0, 4));
  if (!sy || !ey) return [];
  const out: number[] = [];
  for (let y = ey; y >= sy; y -= 1) out.push(y);
  return out;
}

export type Preset = { id: string; label: string; range: DateRange };

/**
 * The Period presets. "All time" only appears when at least one entry has
 * posted (needs `bounds.earliest`) and leads the list — it's the default and
 * most useful for customers with historical uploads.
 */
export function buildPresets(bounds: PeriodBounds | null): Preset[] {
  const list: Preset[] = [
    { id: 'this-month', label: 'This month', range: { from: firstOfMonth(), to: todayIso() } },
    { id: 'last-month', label: 'Last month', range: lastMonthRange() },
    { id: 'ytd', label: 'Year to date', range: { from: firstOfYear(), to: todayIso() } },
    { id: 'last-year', label: 'Last year', range: lastYearRange() },
  ];
  if (bounds?.earliest) {
    list.unshift({
      id: 'all-time',
      label: 'All time',
      range: { from: bounds.earliest, to: todayIso() },
    });
  }
  return list;
}
